import React from 'react';
import {FieldArray, FieldArrayRenderProps, FormikHelpers} from "formik";
import {ToggleSwitch} from "../Control";
import {TimeRangeField} from "../Field";
import Button from "components/Button";
import {TrashIcon} from "@heroicons/react/solid";
import {OpeningHours, StandardHours} from "types/OpeningHoursNew";
import {Info} from "luxon";

// Helpers ----

type Day = {
  name: keyof StandardHours
  label: string
}

function days(): Array<Day> {
  return Info.weekdays().map(day => ({
    name: day.toLowerCase() as keyof StandardHours,
    label: day
  }))
}

export const newRange = '08:00-18:00'
export const allDayRange = '00:00-24:00'

export const alwaysOpen: OpeningHours = {
  monday: [allDayRange],
  tuesday: [allDayRange],
  wednesday: [allDayRange],
  thursday: [allDayRange],
  friday: [allDayRange],
  saturday: [allDayRange],
  sunday: [allDayRange],
  exceptions: [],
  overflow: false
}

export const alwaysClosed: OpeningHours = {
  monday: [],
  tuesday: [],
  wednesday: [],
  thursday: [],
  friday: [],
  saturday: [],
  sunday: [],
  exceptions: [],
  overflow: false
}

// Component ----

type StandardHoursFieldSetProps = {
  name: string
}

function StandardHoursFieldSet({name}: StandardHoursFieldSetProps) {

  function handleToggleDay(day: Day, isOpen: boolean, {setFieldValue}: FormikHelpers<any>) {
    void setFieldValue(`${name}.${day.name}`, isOpen ? [newRange] : [])
  }

  return (
    <fieldset>
      <div className="mb-4">
        <legend className="font-medium">Standard Hours</legend>
        <p className="text-sm text-gray-500">Choose the usual opening times for each day.</p>
      </div>

      {days().map(day => (
        <FieldArray name={`${name}.${day.name}`} key={day.name}>
          {({push, remove, form}: FieldArrayRenderProps) => {
            const timeRanges = form.values[name][day.name] as Array<string>

            return (
              <div key={day.name} className="mb-6">
                <div className="flex items-center space-x-4 mb-1">
                  <ToggleSwitch
                    checked={timeRanges.length > 0}
                    onChange={enabled => handleToggleDay(day, enabled, form)}
                    short
                  />

                  <span className="block text-sm font-medium leading-6 text-gray-900">
                  {day.label}
                </span>
                </div>

                {timeRanges.length < 1 &&
                  <div className="ml-14 text-sm">Closed</div>
                }

                {timeRanges.length > 0 &&
                  <div className="ml-14">
                    <ul>
                      {timeRanges.map((timeRange, rangeIndex) => (
                        <li key={rangeIndex} className="flex items-center space-x-4 mb-1">
                          <TimeRangeField name={`${name}.${day.name}.${rangeIndex}`}/>

                          {timeRanges.length > 1 &&
                            <Button
                              icon={TrashIcon}
                              size={Button.sizes.TINY}
                              color={Button.colors.WHITE}
                              onClick={() => remove(rangeIndex)}
                            />
                          }
                        </li>
                      ))}
                    </ul>

                    {/* TODO: Re-enable this when the audience app supports it */}
                    {/*<div className="mt-1">*/}
                    {/*  <Button*/}
                    {/*    icon={PlusIcon}*/}
                    {/*    size={Button.sizes.TINY}*/}
                    {/*    color={Button.colors.SECONDARY}*/}
                    {/*    onClick={() => push(newRange)}*/}
                    {/*  >*/}
                    {/*    Add Times*/}
                    {/*  </Button>*/}
                    {/*</div>*/}
                  </div>
                }
              </div>
            )
          }}

        </FieldArray>
      ))}
    </fieldset>
  );
}

export default StandardHoursFieldSet;